import React, { createContext, useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { changeLanguage, detectInitialLanguage } from '../utils/i18n'

const LanguageContext = createContext()

/**
 * Context Provider para gerenciamento de idiomas
 * Fornece funcionalidades para trocar idiomas e acessar informações de localização
 */
export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation()
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'pt-BR')
  const [isChangingLanguage, setIsChangingLanguage] = useState(false)
  
  // Idiomas suportados com suas informações
  const supportedLanguages = {
    'pt-BR': {
      code: 'pt-BR',
      name: 'Português',
      nativeName: 'Português (Brasil)',
      flag: '🇧🇷',
      shortCode: 'PT'
    },
    'en-US': {
      code: 'en-US',
      name: 'English',
      nativeName: 'English (US)',
      flag: '🇺🇸',
      shortCode: 'EN'
    },
    'es-ES': {
      code: 'es-ES',
      name: 'Español',
      nativeName: 'Español (España)',
      flag: '🇪🇸',
      shortCode: 'ES'
    }
  }

  // Detectar e aplicar idioma inicial
  useEffect(() => {
    const initializeLanguage = async () => {
      const initialLang = detectInitialLanguage()
      
      if (initialLang !== currentLanguage) {
        await handleLanguageChange(initialLang, false)
      }
    }

    initializeLanguage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sincronizar com mudanças do i18n
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setCurrentLanguage(lng)
      setIsChangingLanguage(false)
    }

    i18n.on('languageChanged', handleLanguageChanged)
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged)
    }
  }, [i18n])

  /**
   * Muda o idioma atual
   * @param {string} newLanguage - Código do novo idioma
   * @param {boolean} reload - Se deve recarregar a página após a mudança
   */
  const handleLanguageChange = async (newLanguage, reload = false) => {
    if (newLanguage === currentLanguage) return
    
    setIsChangingLanguage(true)
    
    try {
      const success = await changeLanguage(newLanguage)
      
      if (success) {
        setCurrentLanguage(newLanguage)
        
        // Recarregar página se solicitado (útil para SEO e URLs)
        if (reload) {
          setTimeout(() => {
            window.location.reload()
          }, 100)
        }
      }
    } catch (error) {
      console.error('Erro ao mudar idioma:', error)
      setIsChangingLanguage(false)
    }
  }

  /**
   * Obtém informações sobre um idioma específico
   * @param {string} languageCode - Código do idioma
   */
  const getLanguageInfo = (languageCode) => {
    return supportedLanguages[languageCode] || null
  }

  /**
   * Obtém informações sobre o idioma atual
   */
  const getCurrentLanguageInfo = () => {
    return getLanguageInfo(currentLanguage)
  }

  /**
   * Verifica se um idioma é suportado
   * @param {string} languageCode - Código do idioma
   */
  const isLanguageSupported = (languageCode) => {
    return Object.keys(supportedLanguages).includes(languageCode)
  }

  /**
   * Obtém lista de todos os idiomas suportados
   */
  const getSupportedLanguages = () => {
    return Object.values(supportedLanguages)
  }

  /**
   * Obtém direção do texto para o idioma atual (LTR/RTL)
   * Preparado para futuras extensões com idiomas RTL
   */
  const getTextDirection = () => {
    // Por enquanto todos os idiomas suportados são LTR
    // Futuramente podemos adicionar árabe, hebraico, etc.
    return 'ltr'
  }

  /**
   * Formata números de acordo com a localização
   * @param {number} number - Número para formatar
   * @param {object} options - Opções de formatação
   */
  const formatNumber = (number, options = {}) => {
    const localeMap = {
      'pt-BR': 'pt-BR',
      'en-US': 'en-US', 
      'es-ES': 'es-ES'
    }

    const locale = localeMap[currentLanguage] || 'pt-BR'
    
    return new Intl.NumberFormat(locale, options).format(number)
  }

  /**
   * Formata moeda de acordo com a localização
   * @param {number} amount - Valor monetário
   * @param {string} currency - Código da moeda (default: BRL)
   */
  const formatCurrency = (amount, currency = 'BRL') => {
    const currencyMap = {
      'pt-BR': 'BRL',
      'en-US': 'USD',
      'es-ES': 'EUR'
    }

    const defaultCurrency = currencyMap[currentLanguage] || currency

    return formatNumber(amount, {
      style: 'currency',
      currency: defaultCurrency,
      minimumFractionDigits: 2
    })
  }

  /**
   * Formata datas de acordo com a localização
   * @param {Date|string} date - Data para formatar
   * @param {object} options - Opções de formatação
   */
  const formatDate = (date, options = {}) => {
    const localeMap = {
      'pt-BR': 'pt-BR',
      'en-US': 'en-US',
      'es-ES': 'es-ES'
    }

    const locale = localeMap[currentLanguage] || 'pt-BR'
    const dateObj = date instanceof Date ? date : new Date(date)

    const defaultOptions = {
      year: 'numeric',
      month: 'long', 
      day: 'numeric',
      ...options
    }

    return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj)
  }

  const value = {
    // Estado atual
    currentLanguage,
    isChangingLanguage,
    
    // Informações de idiomas
    supportedLanguages,
    getCurrentLanguageInfo,
    getLanguageInfo,
    getSupportedLanguages,
    isLanguageSupported,
    
    // Ações
    changeLanguage: handleLanguageChange,
    
    // Utilidades de formatação
    formatNumber,
    formatCurrency,
    formatDate,
    getTextDirection,
    
    // Flags de conveniência
    isPortuguese: currentLanguage === 'pt-BR',
    isEnglish: currentLanguage === 'en-US',
    isSpanish: currentLanguage === 'es-ES'
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

/**
 * Hook para usar o contexto de idioma
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext)
  
  if (!context) {
    throw new Error('useLanguage deve ser usado dentro de um LanguageProvider')
  }
  
  return context
}

export default LanguageContext
